import { supabase } from '@/integrations/supabase/client';

export type FeatureStatus = 'idea' | 'analysis' | 'planned' | 'dev' | 'review' | 'qa' | 'prod';

// Map feature request status → product map item status
function featureStatusToMapStatus(status: FeatureStatus): 'planned' | 'active' | 'deprecated' {
  switch (status) {
    case 'idea':
    case 'analysis':
    case 'planned':
      return 'planned';
    case 'dev':
    case 'review':
    case 'qa':
    case 'prod':
      return 'active';
    default:
      return 'planned';
  }
}

async function syncProductMapItemStatus(featureRequestId: string, newStatus: FeatureStatus) {
  try {
    // Get the linked product_map_item_id
    const { data: fr } = await supabase
      .from('feature_requests' as any)
      .select('product_map_item_id')
      .eq('id', featureRequestId)
      .single();

    const mapItemId = (fr as any)?.product_map_item_id;
    if (!mapItemId) return;

    await supabase
      .from('product_map_items')
      .update({ status: featureStatusToMapStatus(newStatus) })
      .eq('id', mapItemId);
  } catch (e) {
    console.error('Error syncing product map item status:', e);
  }
}

export interface FeatureRequestInsert {
  reporter_id: string;
  title: string;
  description: string;
  priority?: string;
  page_url?: string | null;
  current_view?: string | null;
  screenshot_url?: string | null;
}

export interface FeatureRequestItem {
  id: string;
  reporter_id: string;
  title: string;
  description: string;
  priority: string;
  category: string;
  status: FeatureStatus;
  screenshot_url: string | null;
  page_url: string | null;
  product_map_item_id: string | null;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
  // Joined reporter info
  reporter_first_name: string | null;
  reporter_last_name: string | null;
  reporter_avatar_url: string | null;
  reporter_email: string | null;
  // Assigned tech info
  assigned_first_name: string | null;
  assigned_last_name: string | null;
  assigned_avatar_url: string | null;
  // Analysis info
  analysis_id: string | null;
  analysis_status: string | null;
  implementation_plan: string | null;
  dev_plan: string | null;
  analysis_summary: string | null;
  qa_review: string | null;
}

// Default section ID for new feature requests without a specific location
const DEFAULT_PRODUCT_MAP_SECTION_ID = 'a1000000-0000-0000-0000-000000000001'; // Client App

export async function searchSimilarFeatureRequests(
  description: string
): Promise<{ success: boolean; data?: Array<{ id: string; title: string; description: string; status: string; priority: string; created_at: string }>; error?: string }> {
  try {
    // Get all feature requests and do client-side similarity matching
    const { data, error } = await supabase
      .from('feature_requests' as any)
      .select('id, title, description, status, priority, created_at')
      .order('created_at', { ascending: false });

    if (error) return { success: false, error: error.message };
    if (!data || data.length === 0) return { success: true, data: [] };

    const descLower = description.toLowerCase().trim();
    const descWords = descLower.split(/\s+/).filter(w => w.length > 3);

    const scored = (data as any[]).map(fr => {
      const titleLower = (fr.title || '').toLowerCase();
      const frDescLower = (fr.description || '').toLowerCase();
      const combined = `${titleLower} ${frDescLower}`;

      // Word overlap scoring
      let matchCount = 0;
      for (const word of descWords) {
        if (combined.includes(word)) matchCount++;
      }
      const score = descWords.length > 0 ? matchCount / descWords.length : 0;
      return { ...fr, score };
    });

    // Return items with >40% word overlap
    const similar = scored.filter(s => s.score > 0.4).sort((a, b) => b.score - a.score).slice(0, 5);
    return { success: true, data: similar };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

export async function complementFeatureRequest(
  requestId: string,
  additionalDescription: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: existing, error: fetchErr } = await supabase
      .from('feature_requests' as any)
      .select('description')
      .eq('id', requestId)
      .single();

    if (fetchErr) return { success: false, error: fetchErr.message };

    const currentDesc = (existing as any)?.description || '';
    const newDesc = `${currentDesc}\n\n--- Complément ajouté ---\n${additionalDescription.trim()}`;

    const { error } = await supabase
      .from('feature_requests' as any)
      .update({ description: newDesc } as any)
      .eq('id', requestId);

    if (error) return { success: false, error: error.message };
    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

export async function submitFeatureRequest(
  request: FeatureRequestInsert
): Promise<{ success: boolean; id?: string; error?: unknown }> {
  try {
    const { data, error } = await supabase
      .from('feature_requests' as any)
      .insert(request as any)
      .select('id, title')
      .single();

    if (error) {
      console.error('Error submitting feature request:', error);
      return { success: false, error };
    }

    const featureId = (data as any)?.id;
    const featureTitle = (data as any)?.title || 'Feature sans titre';

    // Auto-create a product map item linked to this feature
    try {
      const { data: mapItem } = await supabase
        .from('product_map_items')
        .insert({
          section_id: DEFAULT_PRODUCT_MAP_SECTION_ID,
          name: featureTitle,
          description: request.description?.substring(0, 200) || null,
          status: 'planned',
        })
        .select('id')
        .single();

      if (mapItem) {
        await supabase
          .from('feature_requests' as any)
          .update({ product_map_item_id: mapItem.id } as any)
          .eq('id', featureId);
      }
    } catch (mapErr) {
      console.error('Error creating product map item:', mapErr);
      // Non-blocking — feature is still created
    }

    return { success: true, id: featureId };
  } catch (error) {
    console.error('Error in submitFeatureRequest:', error);
    return { success: false, error };
  }
}

export async function fetchFeatureRequests(): Promise<{
  success: boolean;
  data?: FeatureRequestItem[];
  error?: string;
}> {
  try {
    const { data: requests, error } = await supabase
      .from('feature_requests' as any)
      .select('*')
      .order('created_at', { ascending: false });

    if (error) return { success: false, error: error.message };
    if (!requests || requests.length === 0) return { success: true, data: [] };

    // Fetch reporter + assigned profiles
    const reporterIds = [...new Set((requests as any[]).map((r) => r.reporter_id))];
    const assignedIds = [...new Set((requests as any[]).map((r) => r.assigned_to).filter(Boolean))];
    const allUserIds = [...new Set([...reporterIds, ...assignedIds])];
    const { data: profiles } = await supabase
      .from('users')
      .select('id, first_name, last_name, avatar_url, email')
      .in('id', allUserIds);

    const profileMap = new Map((profiles || []).map((p) => [p.id, p]));

    // Fetch analyses
    const requestIds = (requests as any[]).map((r) => r.id);
    const { data: analyses } = await supabase
      .from('feature_request_analyses' as any)
      .select('*')
      .in('feature_request_id', requestIds);

    const analysisMap = new Map((analyses as any[] || []).map((a) => [a.feature_request_id, a]));

    const items: FeatureRequestItem[] = (requests as any[]).map((r) => {
      const reporter = profileMap.get(r.reporter_id);
      const assigned = r.assigned_to ? profileMap.get(r.assigned_to) : null;
      const analysis = analysisMap.get(r.id);
      return {
        id: r.id,
        reporter_id: r.reporter_id,
        title: r.title,
        description: r.description,
        priority: r.priority,
        category: r.category,
        status: (r.status || 'idea') as FeatureStatus,
        screenshot_url: r.screenshot_url,
        page_url: r.page_url,
        product_map_item_id: r.product_map_item_id || null,
        assigned_to: r.assigned_to || null,
        created_at: r.created_at,
        updated_at: r.updated_at,
        reporter_first_name: reporter?.first_name || null,
        reporter_last_name: reporter?.last_name || null,
        reporter_avatar_url: reporter?.avatar_url || null,
        reporter_email: reporter?.email || null,
        assigned_first_name: assigned?.first_name || null,
        assigned_last_name: assigned?.last_name || null,
        assigned_avatar_url: assigned?.avatar_url || null,
        analysis_id: analysis?.id || null,
        analysis_status: analysis?.status || null,
        implementation_plan: analysis?.implementation_plan || null,
        dev_plan: analysis?.dev_plan || null,
        analysis_summary: analysis?.analysis_summary || null,
        qa_review: analysis?.qa_review || null,
      };
    });

    return { success: true, data: items };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

export async function updateFeatureRequestStatus(
  requestId: string,
  newStatus: FeatureStatus
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('feature_requests' as any)
      .update({ status: newStatus } as any)
      .eq('id', requestId);

    if (error) return { success: false, error: error.message };

    // Sync product map item status
    await syncProductMapItemStatus(requestId, newStatus);

    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

export async function updateFeatureRequest(
  requestId: string,
  updates: Partial<Pick<FeatureRequestItem, 'title' | 'category' | 'priority' | 'status' | 'product_map_item_id' | 'assigned_to'>>
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('feature_requests' as any)
      .update(updates as any)
      .eq('id', requestId);

    if (error) return { success: false, error: error.message };

    // Sync product map item if status changed
    if (updates.status) {
      await syncProductMapItemStatus(requestId, updates.status as FeatureStatus);
    }

    // Sync product map item name if title changed
    if (updates.title) {
      try {
        const { data: fr } = await supabase
          .from('feature_requests' as any)
          .select('product_map_item_id')
          .eq('id', requestId)
          .single();
        const mapItemId = (fr as any)?.product_map_item_id;
        if (mapItemId) {
          await supabase
            .from('product_map_items')
            .update({ name: updates.title })
            .eq('id', mapItemId);
        }
      } catch (e) {
        console.error('Error syncing product map item name:', e);
      }
    }

    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

export async function deleteFeatureRequest(
  requestId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Delete related AI questions first
    const { data: analysis } = await supabase
      .from('feature_request_analyses' as any)
      .select('id')
      .eq('feature_request_id', requestId)
      .maybeSingle();

    if (analysis) {
      await supabase
        .from('feature_request_ai_questions' as any)
        .delete()
        .eq('analysis_id', (analysis as any).id);

      await supabase
        .from('feature_request_analyses' as any)
        .delete()
        .eq('feature_request_id', requestId);
    }

    const { error } = await supabase
      .from('feature_requests' as any)
      .delete()
      .eq('id', requestId);

    if (error) return { success: false, error: error.message };
    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

export async function updateFeatureRequestAnalysis(
  analysisId: string,
  updates: { analysis_summary?: string; implementation_plan?: string }
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('feature_request_analyses' as any)
      .update(updates as any)
      .eq('id', analysisId);

    if (error) return { success: false, error: error.message };
    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

export interface StaffMember {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
}

export async function fetchStaffMembers(): Promise<{
  success: boolean;
  data?: StaffMember[];
  error?: string;
}> {
  try {
    // Get only tech role user IDs
    const { data: staffRoles, error: rolesError } = await supabase
      .from('user_roles')
      .select('user_id')
      .eq('role', 'tech' as any);

    if (rolesError) return { success: false, error: rolesError.message };
    if (!staffRoles || staffRoles.length === 0) return { success: true, data: [] };

    const staffIds = [...new Set(staffRoles.map(r => r.user_id))];
    const { data: profiles, error: profilesError } = await supabase
      .from('users')
      .select('id, first_name, last_name, avatar_url')
      .in('id', staffIds)
      .order('first_name');

    if (profilesError) return { success: false, error: profilesError.message };
    return { success: true, data: profiles || [] };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}
